import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, CheckCircle, Star, Users, Zap, ExternalLink, Building, Handshake, TrendingUp, Target, DollarSign, Clock, BarChart3, Truck, Phone, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";

export default function PartnersPage() {
  // --- BEGIN FULL CONTENT FROM components/Partners.tsx ---
  // (Insert all arrays, data, and the full return statement here)
  const integrationBenefits = [
    {
      title: "Zoho + Razorpay Integration",
      subtitle: "Seamless Payment Processing",
      icon: CreditCard,
      logo: "/assets/logos/razorpay-logo.png",
      description: "Transform your payment collection process with automated invoicing and real-time payment tracking.",
      businessBenefits: {
        costSavings: "Save ₹50,000+ annually on payment processing fees",
        timeReduction: "85% reduction in payment reconciliation time",  
        efficiency: "Automated invoice generation saves 15 hours/week",
        accuracy: "99% payment accuracy with automated matching"
      },
      keyFeatures: [
        "Automatic invoice generation upon payment",
        "Real-time payment status updates in Zoho",
        "Multi-currency payment support",
        "Automated payment reconciliation",
        "Custom payment workflows",
        "Failed payment retry automation"
      ],
      useCases: [
        "E-commerce businesses with high transaction volumes",
        "Subscription-based service providers",
        "B2B companies with complex billing cycles",
        "International businesses requiring multi-currency support"
      ],
      roi: "300% ROI within 6 months through automated processes"
    },
    {
      title: "Zoho + Shiprocket Integration", 
      subtitle: "Complete Logistics Automation",
      icon: Truck,
      logo: "/assets/logos/shiprocket-logo.webp",
      description: "Streamline your entire fulfillment process from order to delivery with intelligent shipping automation.",
      businessBenefits: {
        costSavings: "40% reduction in shipping costs through rate optimization",
        timeReduction: "90% faster order processing and label generation",
        efficiency: "Automated carrier selection saves 20 hours/week",
        accuracy: "98% reduction in shipping errors"
      },
      keyFeatures: [
        "Automated carrier selection based on cost and speed",
        "Bulk order processing and label generation",
        "Real-time tracking updates to customers",
        "Automated return and exchange management",
        "Inventory sync across multiple warehouses",
        "COD remittance automation"
      ],
      useCases: [
        "E-commerce businesses with daily shipments",
        "Retailers managing multiple sales channels",
        "Manufacturers with direct-to-consumer sales",
        "Dropshipping businesses requiring automation"
      ],
      roi: "250% ROI through reduced shipping costs and operational efficiency"
    },
    {
      title: "Zoho + MyOperator Integration",
      subtitle: "Advanced Communication Management", 
      icon: Phone,
      logo: "/assets/logos/myoperator-logo.png",
      description: "Unify all customer communications with intelligent call routing and comprehensive interaction tracking.",
      businessBenefits: {
        costSavings: "60% reduction in call handling costs",
        timeReduction: "70% faster customer query resolution",
        efficiency: "Automated call logging saves 10 hours/week",
        accuracy: "95% improvement in call data capture"
      },
      keyFeatures: [
        "Click-to-call directly from Zoho CRM",
        "Automatic call logging and recording",
        "Intelligent IVR with CRM integration",  
        "SMS and WhatsApp automation",
        "Call analytics and performance dashboards",
        "Missed call management and follow-up automation"
      ],
      useCases: [
        "Sales teams requiring frequent customer calls",
        "Customer support centers handling high volumes",
        "Real estate agencies managing leads",
        "Healthcare providers with appointment systems"
      ],
      roi: "200% ROI through improved customer conversion and retention"
    }
  ];

  const mainPartners = [
    {
      name: "Zoho Corporation",
      type: "Authorized Partner & Creator Registered",
      logo: "/assets/logos/zoho-partner-badge.png",
      badgeImage: "/assets/logos/zoho-creator-registered-badge.png",
      description: "Official Zoho partner with access to premium features, priority support, and latest product updates.",
      partnershipLevel: "Premium",
      yearsPartner: "5+ Years",
      benefits: ["Direct technical support", "Beta feature access", "Co-marketing opportunities", "Training resources"],
      website: "https://www.zoho.com",
      specializations: ["CRM Implementation", "Analytics Setup", "Creator Development", "Workflow Automation"],
      integrationCount: "40+ Applications"
    },
    {
      name: "Razorpay",
      type: "Payment Gateway Integration Partner",
      logo: "/assets/logos/razorpay-logo.png",
      description: "Leading payment gateway integration for seamless transaction processing in Zoho applications.",
      partnershipLevel: "Gold",
      yearsPartner: "3+ Years", 
      benefits: ["Priority integration support", "Custom connector development", "Reduced transaction fees", "Technical documentation"],
      website: "https://razorpay.com",
      specializations: ["Payment Gateway Integration", "Subscription Management", "Invoice Automation", "Financial Reporting"],
      integrationCount: "500+ Implementations"
    },
    {
      name: "Shiprocket",
      type: "Logistics & Shipping Partner",
      logo: "/assets/logos/shiprocket-logo.webp",
      description: "Comprehensive shipping and logistics automation through seamless Zoho Inventory integration.",
      partnershipLevel: "Preferred",
      yearsPartner: "2+ Years",
      benefits: ["Bulk shipping discounts", "API priority access", "Custom logistics solutions", "Dedicated support"],
      website: "https://shiprocket.in", 
      specializations: ["Shipping Automation", "Inventory Management", "Order Fulfillment", "Return Management"],
      integrationCount: "300+ E-commerce Stores"
    },
    {
      name: "MyOperator", 
      type: "Communication Solutions Partner",
      logo: "/assets/logos/myoperator-logo.png",
      description: "Advanced telephony and communication features integrated with Zoho CRM and support systems.",
      partnershipLevel: "Certified",
      yearsPartner: "3+ Years",
      benefits: ["Advanced call features", "CRM integration tools", "Custom IVR solutions", "Analytics dashboards"],
      website: "https://myoperator.co",
      specializations: ["Call Management", "IVR Systems", "Communication Analytics", "Customer Support"],
      integrationCount: "200+ Business Integrations"
    }
  ];

  const integrationSuccessStories = [
    {
      company: "TechMart E-commerce",
      industry: "E-commerce",
      challenge: "Manual payment processing and order fulfillment causing delays and errors",
      solution: "Zoho + Razorpay + Shiprocket complete integration",
      results: [
        "300% faster order processing",
        "85% reduction in payment reconciliation time", 
        "40% savings on shipping costs",
        "99% reduction in order errors"
      ],
      revenue_impact: "₹50 lakhs additional revenue in first year",
      image: "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?w=400&h=250&fit=crop"
    },
    {
      company: "HealthCare Plus",
      industry: "Healthcare",
      challenge: "Poor patient communication and appointment management inefficiencies",
      solution: "Zoho CRM + MyOperator telephony integration",
      results: [
        "70% improvement in appointment booking",
        "60% reduction in missed appointments",
        "95% patient satisfaction score",
        "50% increase in patient retention"
      ],
      revenue_impact: "₹30 lakhs increased revenue through better patient management",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=250&fit=crop"
    },
    {
      company: "Global Services Corp",
      industry: "Professional Services",
      challenge: "Complex multi-currency billing and international payment processing",
      solution: "Zoho Books + Razorpay international payments integration",
      results: [
        "Automated multi-currency invoicing",
        "90% faster international payment processing",
        "100% accuracy in currency conversions", 
        "70% reduction in accounting overhead"
      ],
      revenue_impact: "₹25 lakhs saved annually in processing costs",
      image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&h=250&fit=crop"
    }
  ];

  const partnershipBenefits: Array<{
    icon: any;
    title: string;
    description: string;
  }> = [
    // ... (partnershipBenefits entries)
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-16 bg-gradient-to-r from-indigo-50 via-white to-purple-50 relative overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Handshake className="h-8 w-8 text-blue-600" />
                  <Badge className="bg-blue-100 text-blue-800">Strategic Partnerships</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight">
                  Powerful Integrations
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 block">
                    Drive Real Business Value
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Our strategic partnerships enable seamless integrations that eliminate silos, 
                  automate workflows, and deliver measurable ROI through connected business operations.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                  <div className="text-3xl font-bold mb-2">₹2Cr+</div>
                  <div className="text-green-100">Client Cost Savings</div>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white">
                  <div className="text-3xl font-bold mb-2">1000+</div>
                  <div className="text-purple-100">Active Integrations</div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://consult.optiwizer.com/#/book_consultation" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg px-8 py-4">
                    Book Free Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <a href="https://wa.me/918447103081?text=I want to see an integration demo" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-2">
                    Request Demo via WhatsApp
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>
            {/* Right Visual */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 hover:scale-105 transition-transform duration-300 group">
                    <ImageWithFallback src="/assets/logos/zoho-partner-badge.png" alt="Zoho Authorized Partner" className="h-16 aspect-[3/1] w-auto mx-auto mb-3 object-contain" />
                    <div className="text-center">
                      <div className="font-semibold text-gray-900 text-sm">Premium</div>
                      <div className="text-xs text-gray-600">Partner</div>
                    </div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 hover:scale-105 transition-transform duration-300">
                    <ImageWithFallback src="/assets/logos/zoho-creator-registered-badge.png" alt="Zoho Creator Registered" className="h-16 w-auto mx-auto mb-3 object-contain" />
                    <div className="text-center">
                      <div className="font-semibold text-gray-900 text-sm">Creator</div>
                      <div className="text-xs text-gray-600">Registered</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full p-3 shadow-lg animate-bounce">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-green-400 to-blue-500 rounded-full p-3 shadow-lg animate-pulse">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Benefits - Detailed Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-100 text-green-800">Integration Benefits</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How Our Integrations Transform Your Business
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the specific benefits and ROI you can achieve with our expertly crafted 
              integrations that connect your entire business ecosystem.
            </p>
          </div>

          <div className="space-y-16">
            {integrationBenefits.map((integration, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${
                index % 2 === 1 ? 'lg:direction-reverse' : ''
              }`}>
                {/* Content Side */}
                <div className={`lg:col-span-7 space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                        <integration.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">{integration.title}</h3>
                      <p className="text-lg text-blue-600 font-medium">{integration.subtitle}</p>
                    </div>
                  </div>

                  <p className="text-lg text-gray-600 leading-relaxed">{integration.description}</p>

                  {/* Business Benefits Grid */}
                  <div className="grid grid-cols-2 gap-4 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                    <div className="text-center">
                      <DollarSign className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <div className="font-bold text-green-800 text-sm mb-1">Cost Savings</div>
                      <div className="text-xs text-green-700">{integration.businessBenefits.costSavings}</div>
                    </div>
                    <div className="text-center">
                      <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <div className="font-bold text-blue-800 text-sm mb-1">Time Reduction</div>
                      <div className="text-xs text-blue-700">{integration.businessBenefits.timeReduction}</div>
                    </div>
                    <div className="text-center">
                      <Zap className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <div className="font-bold text-purple-800 text-sm mb-1">Efficiency</div>
                      <div className="text-xs text-purple-700">{integration.businessBenefits.efficiency}</div>
                    </div>
                    <div className="text-center">
                      <Target className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                      <div className="font-bold text-orange-800 text-sm mb-1">Accuracy</div>
                      <div className="text-xs text-orange-700">{integration.businessBenefits.accuracy}</div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-center space-x-3 mb-4">
                      <BarChart3 className="h-6 w-6 text-blue-600" />
                      <h4 className="font-bold text-blue-900">Expected ROI</h4>
                    </div>
                    <p className="text-blue-800 font-semibold">{integration.roi}</p>
                  </div>
                </div>

                {/* Features & Use Cases Side */}
                <div className={`lg:col-span-5 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <Card className="shadow-xl border-0 overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50 pb-4">
                      <div className="flex items-center justify-center mb-4">
                        <ImageWithFallback src={integration.logo} alt={integration.title} className="h-12 w-auto" />
                      </div>
                      <CardTitle className="text-center text-lg">Integration Features</CardTitle>
                    </CardHeader>
                    
                    <CardContent className="p-6 space-y-6">
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-3">Key Features:</h5>
                        <div className="space-y-2">
                          {integration.keyFeatures.map((feature, idx) => (
                            <div key={idx} className="flex items-start space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5 className="font-semibold text-gray-900 mb-3">Perfect For:</h5>
                        <div className="space-y-2">
                          {integration.useCases.map((useCase, idx) => (
                            <div key={idx} className="flex items-start space-x-2">
                              <Building className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">{useCase}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <a href="https://consult.optiwizer.com/#/book_consultation" target="_blank" rel="noopener noreferrer">
                        <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                          Get This Integration
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-100 text-orange-800">Success Stories</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Real Businesses, Real Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our integration solutions have transformed businesses across industries, 
              delivering measurable ROI and operational excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {integrationSuccessStories.map((story, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-0 shadow-lg overflow-hidden">
                <div className="relative">
                  <img
                    src={story.image}
                    alt={story.company}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-bold text-lg">{story.company}</h3>
                    <Badge className="mt-1 bg-white/20 text-white border-white/30">
                      {story.industry}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6 space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Challenge:</h4>
                    <p className="text-gray-600 text-sm">{story.challenge}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Solution:</h4>
                    <p className="text-blue-600 text-sm font-medium">{story.solution}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 text-sm">Results Achieved:</h4>
                    <div className="space-y-2">
                      {story.results.map((result: string, idx: number) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                          <span className="text-xs text-gray-600">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <div className="flex items-start space-x-2">
                      <DollarSign className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-green-800 text-xs mb-1">Revenue Impact</div>
                        <div className="text-green-700 text-xs">{story.revenue_impact}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Partners - Fixed Mobile Layout */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Our Partners</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Trusted Technology Ecosystem
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our strategic partnerships with industry leaders enable comprehensive integrations 
              with enterprise-grade reliability and dedicated support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {mainPartners.map((partner, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg overflow-hidden">
                <CardContent className="p-4 sm:p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-4 lg:space-x-6 space-y-4 sm:space-y-0">
                    {/* Logo Section */}
                    <div className="flex-shrink-0 sm:w-24 lg:w-auto">
                      <div className="mb-4">
                        <ImageWithFallback 
                          src={partner.logo}
                          alt={partner.name}
                          className="h-8 sm:h-10 lg:h-12 max-w-[110px] w-auto mx-auto sm:mx-0 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.insertAdjacentHTML('afterend', '<div class="h-12 sm:h-14 lg:h-16 flex items-center px-4 bg-blue-500 text-white rounded-lg font-bold text-sm">PARTNER</div>');
                          }}
                        />
                        {/* Remove Creator badge for Zoho partner */}
                      </div>
                      <div className="flex justify-center sm:justify-start">
                        <Badge className={`text-xs sm:text-sm ${
                          partner.partnershipLevel === 'Premium' ? 'bg-yellow-100 text-yellow-800' :
                          partner.partnershipLevel === 'Gold' ? 'bg-orange-100 text-orange-800' :
                          partner.partnershipLevel === 'Preferred' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {partner.partnershipLevel}
                        </Badge>
                      </div>
                    </div>
                    {/* Content Section */}
                    <div className="flex-1 space-y-3 sm:space-y-4 min-w-0">
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 break-words">{partner.name}</h3>
                        <div className="text-sm text-blue-600 font-medium mb-2 break-words">{partner.type}</div>
                        <p className="text-gray-600 text-sm leading-relaxed">{partner.description}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3 sm:gap-4 text-center">
                        <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                          <div className="text-base sm:text-lg font-bold text-blue-600">{partner.yearsPartner}</div>
                          <div className="text-xs text-gray-600">Partnership</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                          <div className="text-base sm:text-lg font-bold text-green-600">{partner.integrationCount}</div>
                          <div className="text-xs text-gray-600">Delivered</div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 text-sm">Our Specializations:</h4>
                        <div className="flex flex-wrap gap-1">
                          {partner.specializations.map((spec, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                        <Building className="mr-2 h-4 w-4" />
                        Explore Integration
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Partnership-Powered Advantages
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Our strategic partnerships translate into direct benefits for your business, 
              ensuring you get the best technology solutions with enterprise-grade support.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnershipBenefits.map((benefit, index) => (
              <div key={index} className="text-center text-white">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full mb-6">
                  <benefit.icon className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-bold mb-3">{benefit.title}</h3>
                <p className="text-blue-100 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
            <TrendingUp className="h-5 w-5 text-green-400" />
            <span className="text-white">Integration-Powered Growth</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Unlock Your Business Potential?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Discover how our certified integrations can eliminate silos, automate processes, 
            and deliver measurable ROI for your business operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="https://consult.optiwizer.com/#/book_consultation" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg px-10 py-4">
                <Target className="mr-2 h-5 w-5" />
                Book Free Consultation
              </Button>
            </a>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-lg px-10 py-4">
                <Users className="mr-2 h-5 w-5" />
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}